import tokenStorage from "../auth-context-provider/tokenStorage";

/**
 * REST API upload function - replaces GraphQL upload
 * Uses direct HTTP request to backend REST endpoint
 */
export const uploadVideoToMatchREST = async (
  matchId: string, 
  file: File, 
  onProgress?: (progress: number) => void,
  title?: string
): Promise<any> => {
  const baseUri = import.meta.env.VITE_GRAPHQL_URI || import.meta.env.REACT_APP_GRAPHQL_URI || "/graphql";
  
  // Extract base URL from GraphQL URI (remove /graphql)
  const apiBaseUrl = baseUri.replace('/graphql', '');
  const uploadUrl = `${apiBaseUrl}/api/videos/upload-video/${matchId}`;
  
  console.log(`🚀 REST Upload starting:`, {
    matchId,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    uploadUrl
  });

  // Get authentication token
  const token = tokenStorage.getToken();
  if (!token) {
    throw new Error('No hay token de autenticación. Por favor, inicia sesión.');
  }

  // Create FormData for the upload
  const formData = new FormData();
  formData.append('video', file);
  
  // Optional: Add title and description
  const timestamp = new Date().toLocaleString();
  const videoTitle = title || `Match Video - ${file.name.replace(/\.[^/.]+$/, "")}`;
  formData.append('title', videoTitle);
  formData.append('description', `Match video uploaded on ${timestamp} via Footik web app`);

  console.log(`📦 FormData prepared with video file`);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const percentCompleted = Math.round((e.loaded / e.total) * 100);
        console.log(`📊 Upload progress: ${percentCompleted}%`);
        onProgress?.(percentCompleted);
      }
    });

    // Handle successful response
    xhr.addEventListener('load', () => {
      console.log(`📨 Response status: ${xhr.status}`);
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          console.log(`✅ REST Upload successful:`, response);
          resolve(response);
        } catch (parseError) {
          console.error('❌ Error parsing response:', parseError);
          reject(new Error('Error processing server response'));
        }
      } else {
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          const errorMessage = errorResponse.error || `HTTP Error ${xhr.status}`;
          console.error(`❌ Upload failed with status ${xhr.status}:`, errorResponse);
          reject(new Error(errorMessage));
        } catch (parseError) {
          reject(new Error(`HTTP Error ${xhr.status}: ${xhr.statusText}`));
        }
      }
    });

    // Handle network errors
    xhr.addEventListener('error', () => {
      console.error('❌ Network error during upload');
      reject(new Error('Error de conexión. Verifica tu conexión a internet.'));
    });

    // Handle timeout
    xhr.addEventListener('timeout', () => {
      console.error('❌ Upload timeout');
      reject(new Error('Tiempo de espera agotado. El archivo puede ser demasiado grande.'));
    });

    // Configure request
    xhr.open('POST', uploadUrl);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.timeout = 5 * 60 * 1000; // 5 minutes timeout

    console.log(`🌐 Sending request to ${uploadUrl}`);
    xhr.send(formData);
  });
};

/**
 * Get videos for a match using REST API
 */
export const getMatchVideosREST = async (matchId: string): Promise<any> => {
  const baseUri = import.meta.env.VITE_GRAPHQL_URI || import.meta.env.REACT_APP_GRAPHQL_URI || "/graphql";
  const apiBaseUrl = baseUri.replace('/graphql', '');
  const videosUrl = `${apiBaseUrl}/api/videos/match/${matchId}/videos`;

  const token = tokenStorage.getToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  console.log(`📋 Getting videos from: ${videosUrl}`);

  try {
    const response = await fetch(videosUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP Error ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Videos retrieved:`, data);
    return data;

  } catch (error) {
    console.error('❌ Error getting videos:', error);
    throw error;
  }
};

/**
 * Delete a video from a match using REST API
 */
export const deleteVideoFromMatchREST = async (matchId: string, videoId: string): Promise<any> => {
  try {
    console.log(`🗑️ Deleting video ${videoId} from match ${matchId} via REST API`);
    
    // Get authentication token using the same method as upload
    const token = tokenStorage.getToken();
    if (!token) {
      throw new Error('No authentication token found. Please log in.');
    }

    // Use the same base URL logic as upload
    const baseUri = import.meta.env.VITE_GRAPHQL_URI || import.meta.env.REACT_APP_GRAPHQL_URI || "/graphql";
    const apiBaseUrl = baseUri.replace('/graphql', '');
    const url = `${apiBaseUrl}/api/videos/match/${matchId}/video/${videoId}`;
    
    console.log(`📡 DELETE Request to: ${url}`);
    console.log(`🔑 Token: ${token.substring(0, 20)}...`);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    console.log(`📥 Response status: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`❌ Delete failed with status ${response.status}:`, errorData);
      throw new Error(errorData.error || errorData.message || `HTTP Error ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Video deleted successfully:`, data);
    return data;

  } catch (error) {
    console.error('❌ Error deleting video:', error);
    throw error;
  }
};